import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router'

import { OrderBodyTypePath } from 'constants/order'

import { OrderBodyType, OrderFields, ResolutionId } from 'types/order'
import { UserRole } from 'types/user'

import { selectCurrentUser } from 'store/users/users.selectors'

import { selectOrderRead } from 'store/order/order.selectors'
import { fetchGetOrderRequest, orderSetInitialState } from 'store/order/order.actions'
import { autoChecksSetInitialState, fetchAutoChecksRequest } from 'store/autoChecks/autoChecks.actions'
import { fetchSelectListsRequest, setInitialStateSelectLists } from 'store/selectLists/selectLists.actions'
import { checkVersionRequest } from 'store/version/version.actions'

import OrderHeader from 'components/organisms/OrderHeader'
import OrderBody from 'components/organisms/OrderBody'
import { FINISH_RESOLUTIONS } from 'components/molecules/OrderSpace/spaces/Header/components/ResolutionForm/constants'

import { showFeedbackWidget } from 'utils/feedback-widget'

export const OrderPage: React.FC = () => {
  const dispatch = useDispatch()
  const { orderNumber } = useParams<{ orderNumber: string }>()
  const currentUser = useSelector(selectCurrentUser)
  const orderRead = useSelector(selectOrderRead)
  const history = useHistory()
  const feedbackShownRef = useRef(false)

  useEffect(() => {
    return () => {
      dispatch(orderSetInitialState())
      dispatch(autoChecksSetInitialState())
      dispatch(setInitialStateSelectLists())
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch(orderSetInitialState())
    dispatch(autoChecksSetInitialState())
    dispatch(fetchSelectListsRequest())
    if (currentUser!.role !== UserRole.CUSTOMER) dispatch(fetchAutoChecksRequest(orderNumber))
  }, [dispatch, orderNumber, currentUser])

  useEffect(() => {
    dispatch(fetchGetOrderRequest({ [OrderFields.ORDER_NUMBER]: orderNumber }))
  }, [dispatch, orderNumber])

  const resolution = orderRead?.[OrderFields.RESOLUTION]

  useEffect(() => {
    if (currentUser?.role !== UserRole.CUSTOMER) return
    if (String(resolution) === ResolutionId.CLARIFY_DATA) return
    if (FINISH_RESOLUTIONS.includes(String(resolution) as ResolutionId)) return

    const interval = setInterval(() => {
      dispatch(fetchGetOrderRequest({ [OrderFields.ORDER_NUMBER]: orderNumber }))
    }, 60 * 1000)

    return () => clearInterval(interval)
  }, [currentUser, dispatch, orderNumber, resolution])

  useEffect(() => {
    if (feedbackShownRef.current) return
    if (currentUser?.role !== UserRole.CUSTOMER) return

    const userId = (currentUser as any)?.id
    const entityId = (orderRead as any)?.id || (orderRead as any)?.[OrderFields.ORDER_NUMBER] || orderNumber
    if (!userId || !entityId) return

    showFeedbackWidget({
      userId: String(userId),
      entityId: String(entityId),
      key: 'order_creation',
      meta: { entity_type: 'order' },
    })
      .then(() => {
        feedbackShownRef.current = true
      })
      .catch((e) => {
        console.error('[FeedbackWidget]', e)
      })
  }, [currentUser, orderNumber, orderRead])

  useEffect(
    () => () => {
      window.localStorage.search = ''
    },
    [],
  )

  const [orderBodyType, setOrderBodyType] = useState(OrderBodyType.ORDER)

  const changeOrderBodyType = useCallback(
    (type: OrderBodyType) => {
      history.push(`/order/${orderNumber}/${OrderBodyTypePath[type]}`)
      setOrderBodyType(type)
    },
    [history, orderNumber],
  )

  useEffect(() => {
    const partsOfTheLocation = history.location.pathname?.split('/').filter((s: string) => s)

    if (partsOfTheLocation.length === 3 && partsOfTheLocation[2]) {
      const bodyType = Object.keys(OrderBodyTypePath).find(
        (key) => OrderBodyTypePath[key as OrderBodyType] === partsOfTheLocation[2],
      ) as OrderBodyType
      setOrderBodyType(bodyType)
    } else {
      setOrderBodyType(OrderBodyType.ORDER)
    }
  }, [history.location.pathname, orderNumber])

  useEffect(() => {
    dispatch(checkVersionRequest())
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="container page-content d-flex flex-column flex-grow-1">
      <OrderHeader orderBodyType={orderBodyType} setOrderBodyType={changeOrderBodyType} />
      <OrderBody type={orderBodyType} />
    </div>
  )
}

export default OrderPage
